import React, { useEffect, useState } from 'react';
import { Col, Input, Row } from 'reactstrap';
import { RichTreeView } from '@mui/x-tree-view';
import useFilter from '../hooks/useFilter';
import useAxiosPrivate from '../hooks/useAxiosPrivate';
import useAuth from "../hooks/useAuth";

export default function Categories() {
    const { auth, setAuth } = useAuth();
    const { filter } = useFilter();
    const axiosPrivate = useAxiosPrivate();
    const [loading, setLoading] = useState(true);
    const [selectAll, setSelectAll] = useState(true);

    const [categories, setCategories] = useState([]);
    const [categoryIds, setCategoryIds] = useState([]);
    const [expandedCategories, setExpandedCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [customSelectedCategories, setCustomSelectedCategories] = useState([]);
    const [treeData, setTreeData] = useState(categories)

    const priorityMap = [2, 3, 37];
    //const priorityMap = ["EMS", "Fire", "Compliance"];

    useEffect(() => {
        const getCategories = async () => {
            if (filter?.formCategories) {
                /*
                here:
                    categories - full tree of objects
                    selectedCategories - list of int (selected)
                    categoryIds - list of int (full)
                filter:
                    categories - list of int (selected) (same as selectedCategories above)
                    formCategories - full tree of objects (same as categories above)
                    formCategoryIds - list of int (full) 
                */
                setCategories(filter.formCategories);
                setTreeData(filter.formCategories);
                setCategoryIds(filter.formCategoryIds);
                setExpandedCategories(filter?.formExpandedCategories);

                if (filter?.selectAllCategories) {
                    setSelectAll(true);
                    setSelectedCategories(filter.formCategoryIds);
                }
                else {
                    setSelectAll(false);
                    setCustomSelectedCategories(filter.categories);
                    setSelectedCategories(filter.categories);
                }
            }
            else {
                try {
                    var path = auth.viewLevel === "adminreports" ? '/api/core/courses/categories' : '/api/core/courses/mycategories';
                    await axiosPrivate.get(path)
                        .then(response => {
                            var categoryOptions = buildTree(response.data);
                            setCategories(categoryOptions);
                            setTreeData(categoryOptions);
                            setCategoryIds(categoryOptions[0].allCategoryIds);
                            setSelectedCategories(categoryOptions[0].allCategoryIds);
                        });
                }
                catch (err) {
                    console.log(err);
                }
            }
        };
        Promise.all([getCategories()]).then(function () {
            setLoading(false);
        });
    }, []);

    const buildTree = (categories) => {
        if (typeof categories === 'undefined' || categories === null || !Array.isArray(categories)) return [];
        var tree = categories.map(category => ({
            id: category.value,
            itemId: category.value.toString(),
            value: category.value,
            label: category.label,
            idParent: category.parentId,
            //disabled: ,
            disableSelection: !category.isAssigned,
            children: buildTree(category.options),
            allCategoryIds: category.allCategoryIds
        }));
        tree = sortOptions(tree);
        return tree;
    };

    const sortOptions = (options) => {
        if (typeof options === 'undefined' || options === null || !Array.isArray(options)) return [];
        return options.sort((a, b) => {
            const isAPriority = priorityMap.includes(a.id);
            const isBPriority = priorityMap.includes(b.id);
            if (isAPriority && !isBPriority) return -1;
            if (!isAPriority && isBPriority) return 1;

            return a.label.localeCompare(b.label);
        });
    }

    const onExpandedCategoriesChange = (event, values) => {
        setExpandedCategories(values);
    };

    const onSelectAllChange = (value) => {
        setSelectAll(value);
        if (value === true) {
            setSelectedCategories(categoryIds);
        }
        else {
            setSelectedCategories(customSelectedCategories);
        }
    };

    const onSelectedCategoriesChange = (event, values) => {
        //parent/child selection relationship
        var newIds = determineIdsToSet(categories, values, selectedCategories);

        setCustomSelectedCategories(newIds);
        setSelectedCategories(newIds);
    }


    const onClearSelectionClick = (event) => {
        setCustomSelectedCategories([]);
        setSelectedCategories([]);
    };

    const onFilterChange = (e) => {
        setTreeData(filterBy(categories, e.target.value));
        setExpandedCategories(categoryIds);
    }
    function filterBy(arr, query) {
        return query
            ? arr.reduce((acc, item) => {
                if (item.children?.length) {
                    const filtered = filterBy(item.children, query)
                    if (filtered.length) return [...acc, { ...item, children: filtered }]
                }

                const { children, ...itemWithoutChildren } = item;
                return item.label?.toLowerCase().includes(query.toLowerCase()) ? [...acc, itemWithoutChildren] : acc
            }, []) : arr
    }

    //following functions handle the parent/child interactions
    //not yet a built in part: https://github.com/mui/mui-x/issues/4821
    //taken from : https://stackblitz.com/edit/mui-react-tree-view-check-children-uncheck-parents?file=Demo.tsx

    function determineIdsToSet(
        items,//: TreeViewBaseItem[],
        newIds,//: string[],
        currentIds,//: string[]
    ) {
        const isDeselectingNode = currentIds.length > newIds.length;
        if (isDeselectingNode) {
            const removed = currentIds.filter((id) => !newIds.includes(id))[0];
            const parentIdsToRemove = getParentIds(items, removed);
            const childIdsToRemove = getSelectedIdsAndChildrenIds(items, [removed]);

            const newIdsWithParentsAndChildrenRemoved = newIds.filter(
                (id) => !parentIdsToRemove.includes(id) && !childIdsToRemove.includes(id)
            );

            return newIdsWithParentsAndChildrenRemoved;
        }

        const added = newIds.filter((id) => !currentIds.includes(id))[0];
        const parent = getParentNode(items, added);
        if (parent) {
            const childIds = parent.children?.map((node) => node.id) ?? [];
            const allChildrenSelected = childIds.every((id) => newIds.includes(id));
            if (allChildrenSelected) {
                return [...getSelectedIdsAndChildrenIds(items, newIds), parent.id];
            }
        }
        return getSelectedIdsAndChildrenIds(items, newIds);
    }

    function getParentIds(
        items,//: TreeViewBaseItem[],
        id//: string
    ) {
        const parentIds = [];//: string[] = [];

        for (const item of items) {
            if (item.children) {
                if (item.children.some((child) => child.id === id)) {
                    // The current item is a parent of the supplied id
                    parentIds.push(item.id);

                    // Recursively call the function for the parent item
                    const grandParentIds = getParentIds(items, item.id);
                    parentIds.push(...grandParentIds);
                } else {
                    // Recursively call the function for the children of the current item
                    const childParentIds = getParentIds(item.children, id);
                    parentIds.push(...childParentIds);
                }
            }
        }

        return parentIds;
    }

    function getParentNode(
        items,//: TreeViewBaseItem[],
        id//: string
    ) { //): TreeViewBaseItem | undefined {
        for (const item of items) {
            if (item.children) {
                if (item.children.some((child) => child.id === id)) {
                    // The current item is the parent of the supplied id
                    return item;
                } else {
                    // Recursively call the function for the children of the current item
                    const parentNode = getParentNode(item.children, id);
                    if (parentNode) {
                        return parentNode;
                    }
                }
            }
        }

        // No parent found
        return undefined;
    }

    function getSelectedIdsAndChildrenIds(
        items,//: TreeViewBaseItem[],
        selectedIds,//: string[]
    ) {
        const selectedIdIncludingChildrenIds = new Set([...selectedIds]);

        for (const item of items) {
            if (selectedIds.includes(item.id)) {
                // Add the current item's id to the result array
                selectedIdIncludingChildrenIds.add(item.id);

                // Recursively call the function for the children of the current item
                if (item.children) {
                    const childrenIds = item.children.map((child) => child.id);
                    const childrenSelectedIds = getSelectedIdsAndChildrenIds(
                        item.children,
                        childrenIds
                    );
                    childrenSelectedIds.forEach((selectedId) =>
                        selectedIdIncludingChildrenIds.add(selectedId)
                    );
                }
            } else if (item.children) {
                // walk the children to see if selections lay in there also
                const childrenSelectedIds = getSelectedIdsAndChildrenIds(
                    item.children,
                    selectedIds
                );
                childrenSelectedIds.forEach((selectedId) =>
                    selectedIdIncludingChildrenIds.add(selectedId)
                );
            }
        }

        return [...Array.from(selectedIdIncludingChildrenIds)];
    }

    return ({
        loading, selectAll, categories, categoryIds, selectedCategories, expandedCategories,
        render:
            <Row>
                <Col xs="6">
                    <h5>Course Categories</h5>
                </Col>
                <Col xs="6">
                    <div>
                        <input className='radio-margin' type='radio' name='categoriesRadio' onChange={() => onSelectAllChange(true)} checked={selectAll} /> Select All
                        <input className='radio-margin' type='radio' name='categoriesRadio' onChange={() => onSelectAllChange(false)} checked={!selectAll} /> Custom Selection
                    </div>
                    <div>
                        {!selectAll ?
                            <div>
                                <label>Categories ({selectedCategories.length} selected) (<span onClick={onClearSelectionClick}>Clear selection</span>)</label>
                                <Input placeholder="Type here to search the categories" onChange={onFilterChange} />
                                <RichTreeView
                                    multiSelect
                                    checkboxSelection={true}
                                    items={treeData}
                                    selectedItems={selectedCategories}
                                    expandedItems={expandedCategories}
                                    onExpandedItemsChange={onExpandedCategoriesChange}
                                    onSelectedItemsChange={onSelectedCategoriesChange}
                                />
                            </div>
                            : null}
                    </div>
                </Col>
            </Row>
    });
}